/**
 * TEI/XML Converter
 * OCR結果をTEI/XML形式に変換するユーティリティ
 */

export interface TEIConversionData {
  title?: string;
  sourceUrl?: string;
  results?: any[];
  xml?: string;
  json?: any;
}

export class TEIConverter {
  /**
   * Static method to convert OCR results to TEI format
   * @deprecated Use instance method convertOCRResults instead
   */
  static convert(data: TEIConversionData): string {
    const converter = new TEIConverter();
    return converter.convertOCRResults(data);
  }

  /**
   * OCR結果の配列を直接TEI/XMLに変換する便利メソッド
   */
  static convertFromOCRResults(
    ocrResults: Array<{
      text?: string;
      json?: any;
      imageUrl?: string;
      imageInfo?: any;
    }>,
    options?: {
      title?: string;
      manifestUrl?: string;
    }
  ): string {
    const converter = new TEIConverter();

    // デバッグ: 入力データを確認
    console.log('convertFromOCRResults input:', {
      resultsCount: ocrResults.length,
      firstResult: ocrResults[0] ? {
        hasText: !!ocrResults[0].text,
        hasJson: !!ocrResults[0].json,
        hasRegions: !!ocrResults[0].json?.regions,
        regionsCount: ocrResults[0].json?.regions?.length || 0
      } : null
    });

    // OCR結果をTEI変換用のフォーマットに整形
    const teiData: TEIConversionData = {
      title: options?.title || `OCR Document - ${ocrResults.length} pages`,
      sourceUrl: options?.manifestUrl,
      results: ocrResults.map((result) => {
        // 新しい構造（document.image.text）と古い構造（regions）の両方に対応
        let detections = [];

        // 新しい構造をチェック
        if (result.json?.document?.image?.text) {
          detections = result.json.document.image.text.map((textRegion: any) => ({
            text: textRegion.text || '',
            box: [textRegion.x, textRegion.y, textRegion.x + textRegion.width, textRegion.y + textRegion.height],
            bbox: [textRegion.x, textRegion.y, textRegion.x + textRegion.width, textRegion.y + textRegion.height]
          }));
        }
        // 古い構造にフォールバック
        else if (result.json?.regions) {
          detections = result.json.regions.map((region: any) => ({
            text: region.text || '',
            box: region.bbox || region.box || [0, 0, 100, 100],
            bbox: region.bbox || region.box || [0, 0, 100, 100]
          }));
        }

        console.log('Mapped result:', {
          hasDetections: detections.length > 0,
          detectionsCount: detections.length
        });

        // 画像サイズも新しい構造から取得
        const imageWidth = result.json?.document?.image?.width || result.imageInfo?.width || 1000;
        const imageHeight = result.json?.document?.image?.height || result.imageInfo?.height || 1000;

        return {
          text: result.text || '',
          imageUrl: result.imageUrl || '',
          imageWidth: imageWidth,
          imageHeight: imageHeight,
          detections: detections
        };
      })
    };

    return converter.convertOCRResults(teiData);
  }

  /**
   * Convert OCR results to TEI/XML format
   */
  convertOCRResults(data: TEIConversionData): string {
    // If we have results with detections or text, process them
    if (data.results && data.results.length > 0) {
      const hasContent = data.results.some(r =>
        r.detections?.length > 0 || r.text || r.xml
      );

      console.log('convertOCRResults check:', {
        hasResults: true,
        resultsCount: data.results.length,
        hasContent: hasContent,
        firstResult: data.results[0] ? {
          hasDetections: !!data.results[0].detections,
          detectionsCount: data.results[0].detections?.length || 0,
          hasText: !!data.results[0].text
        } : null
      });

      if (hasContent) {
        return this.combineTEIResults(data);
      }
    }

    // Fallback to default template if no content
    return this.generateXML(data);
  }

  private combineTEIResults(data: TEIConversionData): string {
    const paragraphElements: string[] = [];
    const surfaceElements: string[] = [];

    data.results?.forEach((result, index) => {
      const pageNumber = index + 1;
      const imageUrl = result.imageUrl || '';
      const imageWidth = result.imageWidth || 1000;
      const imageHeight = result.imageHeight || 1000;

      console.log(`Processing result ${pageNumber}:`, {
        hasDetections: !!result.detections,
        detectionsLength: result.detections?.length || 0,
        hasText: !!result.text,
        imageUrl: imageUrl
      });

      // Add page break element
      let pageContent = `        <pb n="${pageNumber}" facs="${imageUrl}"/>
`;

      // Process detections to add line breaks with zone references
      if (result.detections && result.detections.length > 0) {
        console.log(`Creating surface for page ${pageNumber} with ${result.detections.length} detections`);
        result.detections.forEach((detection: any, detIndex: number) => {
          const lineNumber = detIndex + 1;
          const zoneId = `zone-${pageNumber}-${lineNumber}`;
          const text = detection.text || '';

          if (text) {
            pageContent += `        <lb n="${pageNumber}.${lineNumber}" type="line" corresp="#${zoneId}"/>
`;
            pageContent += `        ${this._escapeXml(text)}
`;
          }
        });

        // Create surface element with zones
        let surfaceXml = `    <surface sameAs="${imageUrl}" ulx="0" uly="0" lrx="${imageWidth}" lry="${imageHeight}">
`;
        surfaceXml += `      <graphic url="${imageUrl}" width="${imageWidth}px" height="${imageHeight}px"/>
`;

        result.detections.forEach((detection: any, detIndex: number) => {
          const lineNumber = detIndex + 1;
          const zoneId = `zone-${pageNumber}-${lineNumber}`;
          // detection.box is [x1, y1, x2, y2] format
          const box = detection.box || detection.bbox || [0, 0, 100, 100];

          // Ensure we have valid coordinates
          const ulx = Math.round(Math.min(box[0], box[2]));
          const uly = Math.round(Math.min(box[1], box[3]));
          const lrx = Math.round(Math.max(box[0], box[2]));
          const lry = Math.round(Math.max(box[1], box[3]));

          surfaceXml += `      <zone xml:id="${zoneId}" ulx="${ulx}" uly="${uly}" lrx="${lrx}" lry="${lry}"/>
`;
        });

        surfaceXml += `    </surface>`;
        surfaceElements.push(surfaceXml);
      } else if (result.text) {
        // Fallback: if no detections but has text, add lines without zones
        const lines = result.text.split('\n');
        lines.forEach((line: string, lineIndex: number) => {
          if (line.trim()) {
            const lineNumber = lineIndex + 1;
            pageContent += `        <lb n="${pageNumber}.${lineNumber}" type="line"/>
`;
            pageContent += `        ${this._escapeXml(line.trim())}
`;
          }
        });
      }

      paragraphElements.push(pageContent);
    });

    // Build combined TEI document
    let tei = `<?xml version="1.0" encoding="UTF-8"?>
<?xml-model href="http://www.tei-c.org/release/xml/tei/custom/schema/relaxng/tei_all.rng" type="application/xml" schematypens="http://relaxng.org/ns/structure/1.0"?>
<?xml-model href="http://www.tei-c.org/release/xml/tei/custom/schema/relaxng/tei_all.rng" type="application/xml" schematypens="http://purl.oclc.org/dsdl/schematron"?>
<TEI xmlns="http://www.tei-c.org/ns/1.0">
  <teiHeader>
    <fileDesc>
      <titleStmt>
        <title>${this._escapeXml(data.title || 'OCR処理結果')}</title>
        <respStmt>
          <resp>Automated Transcription</resp>
          <name ref="https://github.com/ndl-lab/ndlkotenocr-lite">NDL古典籍OCR-Liteアプリケーション</name>
        </respStmt>
      </titleStmt>
      <publicationStmt>
        <p>${data.sourceUrl?.startsWith('http') ? 'Converted from IIIF Manifest' : 'Converted from uploaded images'}</p>
      </publicationStmt>
      <sourceDesc>
        <p>${this._escapeXml(data.sourceUrl || 'User uploaded images')}</p>
      </sourceDesc>
    </fileDesc>
  </teiHeader>
  <text>
    <body>
      <p>
${paragraphElements.join('')}      </p>
    </body>
  </text>`;

    // Add facsimile section if we have surface elements
    console.log('Surface elements check:', {
      count: surfaceElements.length,
      hasSurfaces: surfaceElements.length > 0
    });

    if (surfaceElements.length > 0) {
      const facsimileAttr = data.sourceUrl?.startsWith('http') ? ` sameAs="${this._escapeXml(data.sourceUrl)}"` : '';
      tei += `
  <facsimile${facsimileAttr}>
${surfaceElements.join('\n')}
  </facsimile>`;
    }

    tei += '\n</TEI>';

    return tei;
  }

  private _escapeXml(text: string): string {
    if (!text) return '';
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  private generateXML(data: TEIConversionData): string {
    return `<?xml version="1.0" encoding="UTF-8"?>
<?xml-model href="http://www.tei-c.org/release/xml/tei/custom/schema/relaxng/tei_all.rng" type="application/xml" schematypens="http://relaxng.org/ns/structure/1.0"?>
<?xml-model href="http://www.tei-c.org/release/xml/tei/custom/schema/relaxng/tei_all.rng" type="application/xml" schematypens="http://purl.oclc.org/dsdl/schematron"?>
<TEI xmlns="http://www.tei-c.org/ns/1.0">
  <teiHeader>
    <fileDesc>
      <titleStmt>
        <title>${this._escapeXml(data.title || 'OCR Results')}</title>
        <respStmt>
          <resp>Automated Transcription</resp>
          <name ref="https://github.com/ndl-lab/ndlkotenocr-lite">NDL古典籍OCR-Liteアプリケーション</name>
        </respStmt>
      </titleStmt>
      <publicationStmt>
        <p>${data.sourceUrl?.startsWith('http') ? 'Converted from IIIF Manifest' : 'No source URL provided'}</p>
      </publicationStmt>
      <sourceDesc>
        <p>${this._escapeXml(data.sourceUrl || 'No source information')}</p>
      </sourceDesc>
    </fileDesc>
  </teiHeader>
  <text>
    <body>
      <p>
        <!-- No OCR results available -->
      </p>
    </body>
  </text>
</TEI>`;
  }
}